/**
 * Connecting a mobile device by QR code and a confirmation on a device already signed in (F-0332,
 * ADR-151, `docs/concept-mobile-app.md`, *Connect a mobile device by QR code and confirmation*).
 *
 * The approver (a signed-in web page, the Mac app, or another signed-in mobile device) opens a
 * request and shows a QR code. The QR is F-0237's setup link plus one parameter, `pair`, naming the
 * request: public, single use and two minutes long, never a credential. The new device registers the
 * public half of a key it generated in its secure key store, both screens show the same three-digit
 * code, the approver compares them and approves, and the new device completes by signing the
 * server's nonce. The session it receives is bound to that key: refreshing it needs a fresh
 * signature, so a copied refresh token alone renews nothing.
 *
 * One place for the values the server, the web and the apps must agree on.
 */

/** Where a pairing request is in its life. A closed vocabulary, `CHECK`-constrained in the table. */
export const MOBILE_PAIRING_STATES = [
  'pending_scan',
  'pending_approval',
  'approved',
  'denied',
  'expired',
  'consumed',
] as const;
export type MobilePairingStateType = (typeof MOBILE_PAIRING_STATES)[number];

/**
 * Where the new device keeps its key. All three from the first migration (AC-QR8): Android's two
 * are accepted before an Android client exists, so the protocol was never iOS-only by accident.
 */
export const MOBILE_DEVICE_KEY_STORES = ['secure-enclave', 'strongbox', 'tee-keystore'] as const;
export type MobileDeviceKeyStoreType = (typeof MOBILE_DEVICE_KEY_STORES)[number];

/** How long a request answers at all, from its creation: register, approve and complete included. */
export const MOBILE_PAIRING_REQUEST_TTL_MS = 2 * 60_000;

/**
 * How recently the approver's own session must have been minted (not merely refreshed) for its
 * approval to count without a step-up. A tab left open for hours is not a person present now.
 */
export const MOBILE_PAIRING_RECENT_SIGN_IN_MS = 15 * 60_000;

/** How long a step-up (`/auth/reauth/*`) counts as the approver having just proved who they are. */
export const REAUTH_TOKEN_TTL_MS = 5 * 60_000;

/** The matching code's length. Shown on both screens and compared by eye, never typed. */
export const MOBILE_PAIRING_CODE_LENGTH = 3;

/** The longest device name, model or platform a registration may carry. */
export const MOBILE_DEVICE_FACT_MAX_LENGTH = 80;

/** Why a pairing call was refused, as a code a client can act on. */
export const MOBILE_PAIRING_REFUSALS = {
  /** The approver has to prove they are present: sign in again, a passkey, or their password. */
  REAUTH_REQUIRED: 'reauth_required',
  /** The request is past its two minutes, or a newer request replaced it. Start again. */
  EXPIRED: 'pairing_request_expired',
  /** The request is not in the state this call needs: registered already, denied, or used. */
  WRONG_STATE: 'pairing_request_wrong_state',
} as const;
export type MobilePairingRefusalType =
  (typeof MOBILE_PAIRING_REFUSALS)[keyof typeof MOBILE_PAIRING_REFUSALS];

/**
 * What the new device signs to complete, with its device key (ES256).
 *
 * Domain-separated from the refresh proof below, so a signature made for one can never be presented
 * as the other, and bound to the request id as well as the nonce.
 */
export function mobilePairingCompletionMessage(requestId: string, nonce: string): string {
  return `featit-pair-complete\n${requestId}\n${nonce}`;
}

/** What a paired device signs to refresh its session (ES256), over a nonce from `/auth/refresh/nonce`. */
export function pairedRefreshProofMessage(nonce: string): string {
  return `featit-refresh\n${nonce}`;
}

/** The state a request is really in: a live state past its expiry reads as `expired`. */
export function effectiveMobilePairingState(
  state: MobilePairingStateType,
  expiresAt: Date | string,
  now: number = Date.now(),
): MobilePairingStateType {
  const live = state === 'pending_scan' || state === 'pending_approval' || state === 'approved';
  return live && new Date(expiresAt).getTime() <= now ? 'expired' : state;
}

/** `POST /auth/pair/requests`: what the approver's screen shows. */
export interface IMobilePairingRequestCreated {
  readonly requestId: string;
  readonly code: string;
  readonly expiresAt: string;
  /** The universal link the QR code encodes: F-0237's setup link with `pair`. */
  readonly link: string;
}

/** The new device, as it described itself at registration. */
export interface IMobilePairingDevice {
  readonly name: string;
  readonly model: string;
  readonly platform: string;
  readonly keyStore: MobileDeviceKeyStoreType;
  /**
   * Coarse, and only what the server can say without a location database: the same network as the
   * approver, a private network, or null. Never an address, never a coordinate.
   */
  readonly approximateLocation: string | null;
  readonly registeredAt: string;
}

/** `GET /auth/pair/requests/:id`: the approver's view of their own request. */
export interface IMobilePairingRequestView {
  readonly requestId: string;
  readonly state: MobilePairingStateType;
  readonly code: string;
  readonly expiresAt: string;
  /** Null until the new device has registered. */
  readonly device: IMobilePairingDevice | null;
  /** The session the pairing minted, once completed: the row the devices list shows. */
  readonly credentialId: string | null;
}

/** `POST /auth/pair/requests/:id/register`: what the new device shows and later signs. */
export interface IMobilePairingRegistered {
  readonly state: 'pending_approval';
  readonly code: string;
  readonly nonce: string;
  readonly expiresAt: string;
}

/** `GET /auth/pair/requests/:id/status`: the new device's poll. The state and nothing else. */
export interface IMobilePairingStatus {
  readonly state: MobilePairingStateType;
}

/** `POST /auth/reauth/password` and `/auth/reauth/passkey`: the approver's step-up. */
export interface IReauthGranted {
  readonly reauthToken: string;
  readonly expiresAt: string;
}

/** What a paired device adds to `POST /auth/refresh`: its signature over a fresh server nonce. */
export interface IPairedRefreshProof {
  readonly nonce: string;
  /** Base64url ES256 signature over `pairedRefreshProofMessage(nonce)`, raw (r‖s) or DER. */
  readonly signature: string;
}
